import { Button, Group, I18nProvider, Input, NumberField as AriaNumberField } from 'react-aria-components';
import { useT } from '../../../i18n/hooks';
import { FM } from './messages';
import { NUMBER_LOCALE } from './locale';
import { FieldFoot, FieldLabel, cx, fieldClass } from './parts';
import type { NumberFieldProps } from './types';
import './styles';

/**
 * Число с запятой по-русски («12,5») на всех трёх языках, единица — суффикс
 * внутри поля. Пусто — `null`, а не `NaN`: наружу NaN не выходит. Значение
 * зажимается в `minValue`/`maxValue` при уходе из поля и округляется до
 * `fractionDigits` знаков.
 */
export function NumberField({ label, 'aria-label': ariaLabel, explain, description, errorMessage, isInvalid, isRequired, isDisabled, size = 'md', className, value, onChange, minValue, maxValue, step, unit, fractionDigits = 2, hideStepper = true, placeholder }: NumberFieldProps) {
  const t = useT();
  return (
    <I18nProvider locale={NUMBER_LOCALE}>
      <AriaNumberField
        className={fieldClass(size, className)}
        value={value ?? Number.NaN}
        onChange={(next) => onChange(Number.isNaN(next) ? null : Number(next.toFixed(fractionDigits)))}
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: fractionDigits }}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        isRequired={isRequired}
        validationBehavior="aria"
        aria-label={label ? undefined : ariaLabel}
      >
        <FieldLabel label={label} ariaLabel={ariaLabel} explain={explain} isRequired={isRequired} />
        <Group className={cx('ac-trigger', 'fm-input')} isDisabled={isDisabled} isInvalid={isInvalid}>
          <Input inputMode={fractionDigits > 0 ? 'decimal' : 'numeric'} placeholder={placeholder} />
          {unit ? <span className="fm-unit">{unit}</span> : null}
          {hideStepper ? null : (
            <div className="fm-stepper">
              <Button slot="decrement" aria-label={t(FM.decrease)}>
                −
              </Button>
              <Button slot="increment" aria-label={t(FM.increase)}>
                +
              </Button>
            </div>
          )}
        </Group>
        <FieldFoot description={description} errorMessage={errorMessage} isInvalid={isInvalid} />
      </AriaNumberField>
    </I18nProvider>
  );
}
